import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { FieldConfig } from '../../models/field-configs/field-config.interface';
import { AbstractField } from '../../models/field-configs/abstract-field';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { getObservableFromAsyncValueSafe } from '../../../../models/async-value';

@Component({
  selector: 'form-input',
  styleUrls: ['form-input.component.scss'],
  templateUrl: 'form-input.component.html',
})
export class FormInputComponent extends AbstractField implements OnInit, OnDestroy {
  public config: FieldConfig;
  public group: UntypedFormGroup;
  public fieldControl: UntypedFormControl;
  public placeholder: string;
  public isInformative: boolean;
  public isRequired: boolean;
  public visible = true;
  public shouldShowError = false;

  private ngDestroy = new Subject();

  private updateStatusSubject: Subject<boolean>;

  public showMessage$: Observable<string>;

  private recordMessage: Record<string, string> = {
    'required': 'forms.required',
    'pattern': 'forms.invalid_number',
    'email': 'forms.invalid_email',
    'errorMessage': 'errorMessage',
  };

  ngOnInit(): void {
    this.fieldControl = this.getControl() as UntypedFormControl;

    if (this.config.placeholder) {
      getObservableFromAsyncValueSafe(this.config.placeholder)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((text) => {
          this.placeholder = text;
        });
    }

    if (this.config.value) {
      getObservableFromAsyncValueSafe(this.config.value)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((value) => {
          this.getControl().setValue(value);
        });
    }

    if (this.config.informative !== undefined) {
      this.isInformative = this.config.informative;
    }

    this.fieldControl = this.getControl() as UntypedFormControl;
    this.isRequired = this.hasRequiredField(this.fieldControl);

    if (this.config.asyncValidators) {
      this.fieldControl.statusChanges.pipe(takeUntil(this.ngDestroy)).subscribe(() => {
        this.isRequired = this.hasRequiredField(this.fieldControl) && this.visible;
      });
    }

    if (this.config.visible !== undefined) {
      getObservableFromAsyncValueSafe(this.config.visible)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((fieldVisibility) => (this.visible = fieldVisibility));
    }

    if (this.config.statusAsync) {
      getObservableFromAsyncValueSafe(this.config.statusAsync)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe();
    }

    if (this.config.updateStatusMnaually && this.config.eventUpdateStatus) {
      this.updateStatusSubject = new Subject<boolean>();
      this.showMessage$ = this.updateStatusSubject.pipe(
        filter((update) => update),
        map(() => {
          return this.getMessageError(this.fieldControl);
        }),
        distinctUntilChanged(),
      );
      getObservableFromAsyncValueSafe(this.config.eventUpdateStatus)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((update) => {
          if (update) {
            this.shouldShowError = !!this.fieldControl.errors;
            this.updateStatusSubject.next(this.shouldShowError);
          }
        });
    } else {
      this.showMessage$ = this.fieldControl.statusChanges.pipe(
        map((status) => {
          return this.getMessageError(this.fieldControl);
        }),
        distinctUntilChanged(),
      );
    }
  }

  getMessageError(fieldControl: AbstractControl): string {
    if (fieldControl.hasError('required') && fieldControl.invalid) {
      return this.recordMessage['required'];
    } else if (fieldControl.hasError('pattern') && fieldControl.invalid) {
      return this.recordMessage['pattern'];
    } else if (fieldControl.hasError('email') && fieldControl.invalid) {
      return this.recordMessage['email'];
    } else if (fieldControl.hasError('errorMessage') && fieldControl.invalid) {
      return fieldControl.errors['errorMessage'];
    }

    const statusMessage = this.config?.customErrorMessages ?? [];

    if (statusMessage.length > 0) {
      for (const item of statusMessage) {
        const keyName = Object.keys(item)[0];
        if (fieldControl.hasError(keyName) && fieldControl.invalid) {
          return item[keyName];
        }
      }
    }

    return '';
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
    if (this.updateStatusSubject) {
      this.updateStatusSubject.complete();
    }
  }
}
